import { useState } from 'react'
import type { Aspect, Clip } from '../api/types'
import { useEpisodeCosts } from '../hooks/useBilling'
import { useClips, useDeleteClip, useRenderClip, useSuggestClips } from '../hooks/useClips'
import { Cost } from './Cost'
import { Badge, Button, Card, EmptyState, ErrorNote, LoadingBlock, ProgressBar } from './ui'

const ASPECTS: { value: Aspect; label: string }[] = [
  { value: '9:16', label: 'Vertical 9:16' },
  { value: '1:1', label: 'Square 1:1' },
  { value: '16:9', label: 'Wide 16:9' },
]

/** `m:ss`, or `h:mm:ss` from an hour on. */
function clock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000))
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const seconds = String(total % 60).padStart(2, '0')
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}` : `${minutes}:${seconds}`
}

/** Suggested and hand-picked clips, each rendered on demand in a chosen shape. */
export function ClipsPanel({
  workspaceId,
  episodeId,
  transcriptReady,
  canEdit,
  onSeek,
}: {
  workspaceId: string
  episodeId: string
  transcriptReady: boolean
  canEdit: boolean
  onSeek: (ms: number) => void
}) {
  const clips = useClips(workspaceId, episodeId, transcriptReady)
  const suggest = useSuggestClips(workspaceId, episodeId)
  const suggestCost = useEpisodeCosts(workspaceId, episodeId).data?.text.find((t) => t.kind === 'clips')?.micro_credits
  const [instruction, setInstruction] = useState('')
  const data = clips.data
  const pending = data?.suggest_status === 'pending' || suggest.isPending
  const hasSuggestions = !!data?.clips.some((c) => c.source === 'suggested')
  return (
    <Card
      title="Clips"
      actions={
        canEdit &&
        data?.transcript_ready && (
          <Button variant="secondary" busy={pending} disabled={pending} onClick={() => suggest.mutate({})}>
            {hasSuggestions ? 'Suggest again' : 'Suggest clips'} <Cost micro={suggestCost} />
          </Button>
        )
      }
    >
      {!transcriptReady ? (
        <p className="muted">Clips are found in the transcript. Ask for one on the Transcript card first.</p>
      ) : !data ? (
        clips.isError ? <ErrorNote error={clips.error} /> : <LoadingBlock />
      ) : (
        <div className="stack">
          {suggest.error && <ErrorNote error={suggest.error} />}
          {data.suggest_status === 'failed' && <ErrorNote message={`Finding clips failed: ${data.suggest_error ?? 'no reason was given.'}`} />}
          {pending && <p className="muted" data-testid="clips-pending">Finding moments worth clipping…</p>}
          {data.clips.length === 0 && !pending ? (
            <EmptyState title="No clips yet" body="Suggest clips, or shift-click two words in the transcript to pick one yourself." />
          ) : (
            <div className="clip-list">
              {data.clips.map((clip) => (
                <ClipRow key={clip.id} workspaceId={workspaceId} episodeId={episodeId} clip={clip} canEdit={canEdit} onSeek={onSeek} />
              ))}
            </div>
          )}
          {canEdit && hasSuggestions && (
            <form
              className="row tp-regenerate"
              onSubmit={(event) => {
                event.preventDefault()
                if (instruction.trim()) suggest.mutate({ instruction: instruction.trim() }, { onSuccess: () => setInstruction('') })
              }}
            >
              <input
                value={instruction}
                onChange={(event) => setInstruction(event.target.value)}
                placeholder="Look again with an instruction, e.g. the funniest moments"
                maxLength={1000}
                aria-label="Clip instruction"
                disabled={pending}
              />
              <Button type="submit" variant="secondary" busy={suggest.isPending} disabled={pending || !instruction.trim()}>
                Look again <Cost micro={suggestCost} />
              </Button>
            </form>
          )}
        </div>
      )}
    </Card>
  )
}

function ClipRow({ workspaceId, episodeId, clip, canEdit, onSeek }: { workspaceId: string; episodeId: string; clip: Clip; canEdit: boolean; onSeek: (ms: number) => void }) {
  const render = useRenderClip(workspaceId, episodeId)
  const remove = useDeleteClip(workspaceId, episodeId)
  const renderCost = useEpisodeCosts(workspaceId, episodeId).data?.clips.find((c) => c.clip_id === clip.id)?.micro_credits
  const [aspect, setAspect] = useState<Aspect>(clip.aspect)
  const rendering = clip.render_status === 'rendering' || render.isPending
  const seconds = Math.round((clip.end_ms - clip.start_ms) / 1000)
  const shape = clip.aspect === '9:16' ? 'portrait' : clip.aspect === '1:1' ? 'square' : 'wide'
  return (
    <div className="clip" data-testid="clip">
      <div className="clip-main stack-sm">
        <div className="row between">
          <strong>{clip.title || 'Untitled clip'}</strong>
          {clip.score != null && <Badge tone={clip.score >= 75 ? 'ok' : 'neutral'}>{clip.score}</Badge>}
        </div>
        <div className="muted small">
          <button type="button" className="tx-link" onClick={() => onSeek(clip.start_ms)} title="Play from here">
            {clock(clip.start_ms)}–{clock(clip.end_ms)}
          </button>{' '}
          · {seconds} s · {clip.source === 'manual' ? 'Picked by hand' : 'Suggested'}
        </div>
        {clip.hook && <p className="clip-hook">{clip.hook}</p>}
        {clip.why && <p className="muted small clip-why">{clip.why}</p>}
        {rendering && (
          <div className="stack-sm">
            <ProgressBar value={clip.progress?.percent ?? 0} label="Rendering" />
            <span className="muted small">Rendering · {Math.floor(clip.progress?.percent ?? 0)}%</span>
          </div>
        )}
        {clip.render_status === 'failed' && <span className="small error-text">The render failed: {clip.render_error}</span>}
        {canEdit && (
          <div className="row">
            <select value={aspect} onChange={(event) => setAspect(event.target.value as Aspect)} aria-label="Shape" disabled={rendering}>
              {ASPECTS.map((a) => (
                <option key={a.value} value={a.value}>
                  {a.label}
                </option>
              ))}
            </select>
            <Button variant="secondary" busy={rendering} disabled={rendering} onClick={() => render.mutate({ clipId: clip.id, aspect })}>
              {clip.render_status === 'ready' ? 'Render again' : 'Render'} <Cost micro={renderCost} />
            </Button>
            <Button variant="ghost" busy={remove.isPending} onClick={() => remove.mutate(clip.id)}>
              Delete
            </Button>
          </div>
        )}
        {(render.error ?? remove.error) && <ErrorNote error={render.error ?? remove.error} />}
      </div>
      {clip.render_status === 'ready' && clip.video_url && (
        <div className={`clip-video clip-${shape}`}>
          <video src={clip.video_url} poster={clip.poster_url ?? undefined} controls playsInline preload="none" data-testid="clip-video" />
          <a href={clip.video_url} target="_blank" rel="noreferrer" className="small">
            Download
          </a>
        </div>
      )}
    </div>
  )
}
